import { useCallback, useEffect, useMemo, useState } from "react";
import ImpersonationServices from "../../../services/ImpersonationServices";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import DashboardLayout from "../../common/DashboardLayout";
import CertificationServices from "../../../services/CertificationServices";
import TeacherServices from "../../../services/TeacherServices";

const extractTeacherRecords = (payload) => {
  const list = (() => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.certifications)) return payload.certifications;
    if (Array.isArray(payload?.teachers)) return payload.teachers;
    if (Array.isArray(payload?.records)) return payload.records;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  })();

  return list
    .map((entry) => {
      const teacher =
        entry.teacher || entry.teacherInfo || entry.user || entry.profile || {};
      // Backend returns certifications in "records" field, not "certifications"
      const certifications = Array.isArray(entry.records)
        ? entry.records
        : Array.isArray(entry.certifications)
          ? entry.certifications
          : [];
      const teacherId =
        teacher.id ||
        teacher._id ||
        entry.teacherId ||
        entry.userId ||
        entry.id ||
        "";
      if (!teacherId) {
        return null;
      }
      return {
        teacherId: String(teacherId), // Ensure consistent string format for comparison
        teacherName:
          teacher.name ||
          teacher.fullName ||
          `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||
          entry.teacherName ||
          "Unknown Teacher",
        teacherEmail: teacher.email || entry.teacherEmail || "",
        isSchoolAdmin: (teacher.roles && (teacher.roles.includes("1") || teacher.roles.includes(1))) || false,
        certifications,
      };
    })
    .filter(Boolean)
    // FILTER: Only show NON-admins (Role 2 only, or distinct from admin management)
    .filter(teacher => !teacher.isSchoolAdmin);
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const summarizeCertifications = (certifications = []) => {
  return certifications.reduce(
    (summary, cert) => {
      const status = (cert.status || "").toLowerCase();
      const isCompleted = ["completed", "approved"].includes(status);

      if (isCompleted) {
        summary.completed += 1;
        // Check for earliest renew date for completed items
        // Only ever report a renew date that was actually set. This used to fall
        // back to calculateDefaultDates(), which invented a date from a hardcoded
        // 2026 calendar and displayed it as though a school admin had chosen it.
        const renewDateStr = cert.renewByDate;

        if (renewDateStr) {
          const renewTime = new Date(renewDateStr).getTime();
          if (!Number.isNaN(renewTime) && (summary.nextRenew === null || renewTime < summary.nextRenew)) {
            summary.nextRenew = renewTime;
          }
        }
      } else {
        if (status === "pending_review") {
          summary.pendingReview += 1;
        } else if (status === "in_progress") {
          summary.inProgress += 1;
        }

        // Check for earliest due date for ANY not-completed item
        // Same here: no stored due date means no due date. Fabricating one made
        // every unstarted core course render red and overdue against a deadline
        // nobody had set, and no edit could clear it.
        const dueDateStr = cert.dueByDate;

        if (dueDateStr) {
          const dueTime = new Date(dueDateStr).getTime();
          if (!Number.isNaN(dueTime) && (summary.nextDue === null || dueTime < summary.nextDue)) {
            summary.nextDue = dueTime;
          }
        }
      }

      const updatedAt = cert.updatedAt || cert.dateCompleted || cert.createdAt;
      if (updatedAt) {
        const timestamp = new Date(updatedAt).getTime();
        if (!Number.isNaN(timestamp) && timestamp > summary.latestUpdate) {
          summary.latestUpdate = timestamp;
        }
      }
      return summary;
    },
    {
      total: certifications.length,
      completed: 0,
      pendingReview: 0,
      inProgress: 0,
      latestUpdate: 0,
      nextDue: null,
      nextRenew: null
    }
  );
};

const MyTeachers = () => {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await CertificationServices.getSchoolCertifications();
      const normalized = extractTeacherRecords(response);
      setRecords(normalized);
      if (!normalized.length) {
        // Only show alert if truly empty (no teachers), 
        // silently handling if only admins exist is better UX than "No teachers" if looking for teachers.
        // But here we filtered admins out, so "No teachers" is accurate.
      }
    } catch (err) {
      console.error("Failed to load teacher certifications", err);
      setError(
        "We couldn't load teacher certifications. Please try again in a moment."
      );
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const filteredRecords = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return records
      .map((record) => {
        const summary = summarizeCertifications(record.certifications);
        return { ...record, summary };
      })
      .filter((record) => {
        const matchesSearch =
          !term ||
          record.teacherName.toLowerCase().includes(term) ||
          record.teacherEmail.toLowerCase().includes(term);
        if (!matchesSearch) return false;

        if (statusFilter === "all") return true;
        const { summary } = record;
        switch (statusFilter) {
          case "completed":
            return summary.completed > 0;
          case "pending_review":
            return summary.pendingReview > 0;
          case "in_progress":
            return summary.inProgress > 0;
          default:
            return true;
        }
      });
  }, [records, searchTerm, statusFilter]);

  const userobj = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <DashboardLayout
      role={1}
      userType={userobj?.type}
      activeMenu="My Teachers"
      layoutType="topnav"
    >
      <div className="card">
        <div className="card-body">
          <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between align-items-lg-center mb-4">
            <div>
              <h4 className="mb-1">Teacher certification tracker</h4>
              <p className="text-muted mb-0">
                Review certification progress for every staff member and
                jump into detailed actions.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <input
                type="search"
                className="form-control"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <select
                className="form-select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="completed">Completed</option>
                <option value="pending_review">Pending review</option>
                <option value="in_progress">In progress</option>
              </select>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={loadRecords}
                disabled={isLoading}
              >
                {isLoading ? "Refreshing..." : "Refresh"}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={isLoading}
                onClick={async () => {
                  const { value: formValues } = await Swal.fire({
                    title: 'Add New Teacher',
                    html:
                      '<div class="text-start">' +
                      '<label class="form-label">Full Name</label>' +
                      '<input id="swal-input-name" class="form-control mb-3" placeholder="e.g. John Doe">' +
                      '<label class="form-label">Email Address</label>' +
                      '<input id="swal-input-email" class="form-control mb-3" placeholder="e.g. john@example.com">' +
                      '</div>',
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: 'Add Teacher',
                    preConfirm: () => {
                      const name = document.getElementById('swal-input-name').value;
                      const email = document.getElementById('swal-input-email').value;

                      if (!name || !email) {
                        Swal.showValidationMessage('Name and Email are required');
                        return false;
                      }
                      return { name, email };
                    }
                  });

                  if (formValues) {
                    try {
                      setIsLoading(true);
                      await TeacherServices.addSchoolTeacher(formValues.name, formValues.email);
                      await Swal.fire({
                        icon: 'success',
                        title: 'Teacher Added',
                        text: `${formValues.name} has been added successfully.`,
                        timer: 2000,
                        showConfirmButton: false
                      });
                      loadRecords();
                    } catch (error) {
                      console.error(error);
                      Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: error.response?.data?.error || 'Failed to add teacher.',
                      });
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
              >
                <i className="bx bx-plus me-1"></i> Add Teacher
              </button>
            </div>
          </div>
          {error && (
            <div className="alert alert-warning mb-4">{error}</div>
          )}
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th>Teacher</th>
                  <th>Email</th>
                  <th>Assigned</th>
                  <th>Completed</th>
                  <th>Next Due</th>
                  <th>Next Renew</th>
                  <th>Last Activity</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Loading...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No teachers match your filters yet.
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record) => {
                    const summary = record.summary;
                    const latestDate = summary.latestUpdate
                      ? formatDate(summary.latestUpdate)
                      : "—";
                    return (
                      <tr key={record.teacherId}>
                        <td>
                          <span className="fw-semibold">
                            {record.teacherName}
                          </span>
                        </td>
                        <td>
                          {record.teacherEmail ? (
                            <a
                              href={`mailto:${record.teacherEmail}`}
                              className="text-decoration-none"
                            >
                              {record.teacherEmail}
                            </a>
                          ) : (
                            <span className="text-muted">
                              No email on file
                            </span>
                          )}
                        </td>
                        <td>{summary.total}</td>
                        <td>{summary.completed}</td>
                        <td className={summary.nextDue && summary.nextDue < Date.now() ? "text-danger fw-bold" : ""}>
                          {summary.nextDue ? formatDate(summary.nextDue) : "—"}
                          {summary.nextDue && summary.nextDue < Date.now() && <i className="bx bx-error-circle ms-1" title="Overdue"></i>}
                        </td>
                        <td className={summary.nextRenew && summary.nextRenew < Date.now() ? "text-danger fw-bold" : ""}>
                          {summary.nextRenew ? formatDate(summary.nextRenew) : "—"}
                          {summary.nextRenew && summary.nextRenew < Date.now() && <i className="bx bx-error ms-1" title="Expired/Renew Needed"></i>}
                        </td>
                        <td>{latestDate}</td>
                        <td className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <Link
                              to={`/editTeacher?teacher_id=${record.teacherId}`}
                              className="btn btn-outline-secondary btn-sm"
                            >
                              Edit
                            </Link>
                            <Link
                              to={`/teacher-certifications-dashboard?teacherId=${record.teacherId}`}
                              className="btn btn-outline-primary btn-sm"
                            >
                              Manage certifications
                            </Link>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={async () => {
                                const result = await Swal.fire({
                                  title: 'Delete Teacher?',
                                  text: `Are you sure you want to delete ${record.teacherName}? This action cannot be undone.`,
                                  icon: 'warning',
                                  showCancelButton: true,
                                  confirmButtonColor: '#d33',
                                  cancelButtonColor: '#3085d6',
                                  confirmButtonText: 'Yes, delete',
                                  cancelButtonText: 'Cancel'
                                });

                                if (result.isConfirmed) {
                                  try {
                                    setIsLoading(true);
                                    await TeacherServices.deleteTeacher(record.teacherId);
                                    await Swal.fire({
                                      icon: 'success',
                                      title: 'Deleted',
                                      text: `${record.teacherName} has been deleted successfully.`,
                                      timer: 2000,
                                      showConfirmButton: false
                                    });
                                    // Reload the teacher list
                                    await loadRecords();
                                  } catch (error) {
                                    console.error('Error deleting teacher:', error);
                                    Swal.fire({
                                      icon: 'error',
                                      title: 'Error',
                                      text: error.response?.data?.error || 'Failed to delete teacher. Please try again.',
                                    });
                                  } finally {
                                    setIsLoading(false);
                                  }
                                }
                              }}
                              disabled={isLoading}
                            >
                              Delete
                            </button>
                            {/* Sysadmin only. Support has otherwise had to infer what a
                                customer sees from code and row counts; this shows them
                                the actual screen, read-only. */}
                            {String(localStorage.getItem("userrole")) === "admin" && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                title="Open this teacher's account read-only, to see what they see"
                                disabled={isLoading}
                                onClick={async () => {
                                  const { value: reason } = await Swal.fire({
                                    title: `View as ${record.teacherName || record.teacherEmail}?`,
                                    input: "text",
                                    inputLabel: "Why? (recorded in the audit log)",
                                    inputPlaceholder: "e.g. checking a reported problem with due dates",
                                    html: '<div class="text-muted small text-start">'
                                      + 'You will see their screens exactly as they do. '
                                      + 'The session is <b>read-only</b> and expires automatically.</div>',
                                    showCancelButton: true,
                                    confirmButtonText: "View as user"
                                  });
                                  if (reason === undefined) return;
                                  try {
                                    setIsLoading(true);
                                    const started = await ImpersonationServices.start({
                                      userId: record.teacherId,
                                      email: record.teacherEmail,
                                      reason,
                                      name: record.teacherName
                                    });
                                    window.location.href = started.landing;
                                  } catch (error) {
                                    Swal.fire({
                                      icon: "error",
                                      title: "Could not start",
                                      text: error.response?.data?.error || error.message
                                        || "Failed to start the session."
                                    });
                                  } finally {
                                    setIsLoading(false);
                                  }
                                }}
                              >
                                View as
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="alert alert-info mt-3 mb-0">
        Tip: Each teacher can log into their portal and upload certificates
        directly, which will appear here in real time.
      </div>
    </DashboardLayout>
  );
};

export default MyTeachers;
